import { SerializedNode } from '@craftjs/core';
import { Theme } from '@descope/page-editor-components';
import { ICommon } from 'types/common';

export const START_TASK_ID = 'start';
export const END_TASK_ID = 'logged-in';
export const NEW_SCREEN_PREFIX = 'new-';
export const END_GROUP_TASK_SUFFIX = '.end';
export const ACTION_NODE_WIDTH = 300;
export const OAUTH_GROUP_INTERACTION_ID = 'oauth';
export const BIOMETRICS_GROUP_INTERACTION_ID = 'biometrics';
export const POLLING_GROUP_INTERACTION_ID = 'polling';
export const FLOW_ACTION_CUSTOM_CLAIM = 'custom-claims';
export const FLOW_ACTIONS_INFO = [FLOW_ACTION_CUSTOM_CLAIM];
export const FLOW_ACTIONS_AUTOMATICALLY_OPEN = [FLOW_ACTION_CUSTOM_CLAIM];

export enum TaskType {
	screen = 'screen',
	automated = 'automated',
	condition = 'condition'
}

export enum VariableType {
	inline = 'inline',
	context = 'context'
}

export enum InteractionType {
	facebook = 'facebook',
	google = 'google',
	microsoft = 'microsoft',
	github = 'github',
	gitlab = 'gitlab',
	apple = 'apple',
	discord = 'discord',
	linkedin = 'linkedin',
	biomertrics = 'fingerprint',
	passkey = 'passkey',
	sso = 'sso'
}

export interface IFlowData {
	isBack?: boolean;
	marked?: boolean;
}

export interface INodeData extends IFlowData {
	hover?: boolean;
	label?: string;
	inputs?: IFlowTaskInput[];
	interactions?: ITaskInteraction[];
}

export interface ITaskNodeData extends INodeData {
	onEditTask?: (task: Task) => void;
	onDeleteTask?: (task: Task) => void;
	task?: Task;
}

export interface IEdgeData extends IFlowData {
	type: string | undefined;
	onDelete: (id: string) => void;
	nextTask: Task;
}

export enum NextActionTaskType {
	error = 'error',
	success = 'success'
}

export enum HiddenActionInputs {
	finishStepId = 'finishStepId',
	exchangeStepId = 'exchangeStepId',
	verifyStepId = 'verifyStepId',
	crossDevice = 'crossDevice'
}

export interface IGroupTasks {
	id: string;
	members: string[];
}

export type TaskActionType = `${NextActionTaskType}` | `${InteractionType}`;

export interface Rule {
	interactionId?: string;
	taskId: string;
}

export interface Next {
	rules: Rule[];
}

export interface IVariable {
	type: VariableType;
	value: any;
	required?: boolean;
}

export interface IAtomicCondition {
	operator: string;
	target: IVariable;
	predicate: IVariable;
}

export interface ITaskCondition {
	interactionId: string;
	name: string;
	atomicConditions: IAtomicCondition[];
	logicalOr?: boolean;
}

export type ITaskConditions = Array<ITaskCondition>;

export interface Task {
	id?: string;
	name: string;
	type?: TaskType;
	action?: string;
	screenId?: string;
	arguments?: Record<string, IVariable>;
	view?: {
		x?: number;
		y?: number;
	};
	next?: Next;
	conditions?: ITaskConditions;
	members?: string[];
}

export interface Tasks extends Record<string, Task> {}

export interface IFlowDSL {
	startTask: string;
	tasks?: Tasks;
}

export interface IFlow extends ICommon {
	name: string;
	description?: string;
	dsl: IFlowDSL;
	disabled: boolean;
}

export interface IFlowsResponse {
	total: number;
	flows: IFlow[];
}

export enum TaskInputType {
	string = 'string',
	number = 'number',
	boolean = 'boolean',
	array = 'array',
	object = 'object',
	context = 'context'
}

export interface IFlowTaskInput {
	type: `${TaskInputType}`;
	name: string;
	required?: boolean;
	displayName?: string;
	visible?: boolean;
}

export interface IFlowActionError {
	type: string;
	displayName: string;
}

export interface IFlowActionSuccess {
	id?: string;
	displayName?: string;
}

export interface IFlowAction {
	name: string;
	displayName: string;
	inputs?: IFlowTaskInput[];
	errors?: IFlowActionError[];
	successes?: IFlowActionSuccess[];
	description: string;
	hidden: boolean;
	members?: string[];
	tags?: string[];
	depercated?: boolean;
	alternatives?: string[];
}

export interface IFlowActions {
	[key: string]: IFlowAction;
}

export interface IFlowsMetadata {
	actions: IFlowActions;
	conditions: IFlowConditions;
}

export interface IFlowCondition {
	ID: string;
	name: string;
	description: string;
	targetOnly: boolean;
	applicableOn: string[];
}

export interface IFlowConditions {
	[key: string]: IFlowCondition;
}
export interface IFlowTheme {
	id: string;
	version: string;
	cssTemplate: { light: Theme; dark: Theme };
}
export interface IFlowInteraction {
	type?: `${InteractionType}`;
	icon?: string;
	subType?: 'social';
	label: string;
	id: string;
}

export interface ITaskInteraction {
	label?: string;
	nextTask?: Task;
	icon?: string;
	type?: TaskActionType;
	id: string;
}

export interface IFlowInteractionGroup extends ITaskInteraction {
	interactions: IFlowInteraction[];
	isCollapsible?: boolean;
}

export interface IScreenMetadata {
	htmlTemplate?: Record<string, SerializedNode>;
	interactions?: IFlowInteraction[];
}
export interface IFlowScreen extends ICommon, IScreenMetadata {
	flowId: string;
	inputs?: IFlowTaskInput[];
}

export interface IFlowScreens {
	[key: string]: IFlowScreen;
}

export interface IFlowUpdateRequest {
	flow: IFlow;
	screens?: IFlowScreen[];
}

export interface IFlowDisableStateUpdateRequest {
	disable: boolean;
}

export interface IExportedFlow {
	flow: IFlow;
	screens?: IFlowScreen[];
}
